import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ElasticsearchSelect from '@client/components/ui/select/UniversalElasticsearchSelect';
import AggregationSelect from '@client/components/ui/select/AggregationSelect';
import RangeSelect from '@client/components/ui/select/RangeSelect';
import CultureTreeSelect from '@client/components/ui/select/CultureTreeSelect';

/**
 * Converts filterLabels from {valueId: label} format to [{value, label}] array format
 * that select components expect for initialLabels
 * @param {Object} labelsObject - Labels object with {valueId: label} structure
 * @returns {Array|null} Array of {value, label} objects or null if no labels
 */
const convertLabelsToArray = (labelsObject) => {
  if (!labelsObject || typeof labelsObject !== 'object') return null;
  const entries = Object.entries(labelsObject);
  if (entries.length === 0) return null;
  return entries.map(([value, label]) => ({ value, label }));
};

/**
 * Reusable filter select component
 * @param {Object} config - Filter configuration
 * @param {Object} filters - Current filter values
 * @param {Function} onFilterChange - Filter change handler
 * @param {string} document - Elasticsearch document type
 * @param {Object} aggregations - Aggregation data from API response
 * @param {Object} filterLabels - Labels for cold start restoration
 * @param useAggregations
 * @returns {JSX.Element}
 */
const FilterSelect = ({
  config,
  filters,
  onFilterChange,
  document,
  aggregations,
  filterLabels,
}) => {
  const { t } = useTranslation();
  const languageConfig = config.languageConfig || null;

  const aggregationOptions = aggregations?.getOptions
    ? aggregations.getOptions(config.key)
    : [];
  const hasAggregationData =
    aggregations && aggregationOptions && aggregationOptions.length > 0;

  // Convert filterLabels from {valueId: label} to [{value, label}] format
  const initialLabelsArray = useMemo(() => {
    return convertLabelsToArray(filterLabels?.[config.key]);
  }, [filterLabels, config.key]);

  if (!config.type && config?.aggregate) {
    return (
      <AggregationSelect
        filterKey={config.key}
        options={aggregationOptions}
        value={filters[config.key] || []}
        onChange={(values, selectedItems) => {
          onFilterChange(config.key, values, selectedItems);
        }}
        label={config.labelKey}
        showCount={true}
        initialLabels={initialLabelsArray}
      />
    );
  }

  if (config.type === 'boolean_select') {
    return (
      <ElasticsearchSelect
        document={document}
        label={config.labelKey}
        field={config.field}
        value={filters[config.key] || []}
        onChange={(values, selectedItems) => {
          const transformedItems = selectedItems?.map((item) => ({
            ...item,
            label:
              item.value === true || item.value === 'true'
                ? 'Oui'
                : item.value === false || item.value === 'false'
                  ? 'Non'
                  : item.label,
          }));
          onFilterChange(config.key, values, transformedItems);
        }}
        mode="multiple"
        allowClear
        maxTagCount={1}
        pageSize={20}
        sortBy="count"
        sortOrder="desc"
        showCount={false}
        infinite={true}
        style={{ width: '100%' }}
        labelTransform={(label, value) =>
          ({ true: 'Oui', false: 'Non' })[String(value)] ?? label
        }
      />
    );
  }

  if (config.type === 'range_select') {
    return (
      <RangeSelect
        label={config.labelKey}
        value={filters[config.key] || []}
        onChange={(values, selectedItems) => {
          onFilterChange(config.key, values, selectedItems);
        }}
        mode="multiple"
        allowClear
        maxTagCount={1}
        style={{ width: '100%' }}
      />
    );
  }

  if (config.type === 'tree_select') {
    return (
      <CultureTreeSelect
        label={config.labelKey}
        endpoint={config.endpoint}
        value={filters[config.key] || []}
        onChange={(values, selectedItems) =>
          onFilterChange(config.key, values, selectedItems)
        }
        placeholder={t('common|Search...')}
        isMulti={true}
        labelField={config.label_field || 'french_name'}
        valueField={config.value_field || 'id'}
        languageFields={languageConfig?.fields}
        languageLabels={languageConfig?.labels}
        defaultLanguage={languageConfig?.defaultLanguage || 'fr'}
        showLanguageSelector={!!languageConfig}
        style={{ width: '100%' }}
        enrichedCultures={initialLabelsArray}
      />
    );
  }

  return (
    <ElasticsearchSelect
      document={document}
      label={config.labelKey}
      field={config.field}
      label_field={config.label_field || null}
      value_field={config.value_field || null}
      value={filters[config.key] || []}
      onChange={(values, selectedItems) =>
        onFilterChange(config.key, values, selectedItems)
      }
      mode="multiple"
      allowClear
      maxTagCount={1}
      pageSize={20}
      sortBy="count"
      sortOrder="desc"
      showCount={false}
      infinite={true}
      style={{ width: '100%' }}
      languageFields={languageConfig?.fields}
      languageLabels={languageConfig?.labels}
      defaultLanguage={languageConfig?.defaultLanguage || 'fr'}
      showLanguageSelector={!!languageConfig}
      initialLabels={initialLabelsArray}
    />
  );
};

/**
 * Reusable filter section component
 * @param {Object} props
 * @param {Array} props.filterConfigs - Array of filter configurations
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onFilterChange - Filter change handler
 * @param {string} props.document - Elasticsearch document type
 * @param {string} props.className - Optional CSS class
 * @param {Object} props.aggregations - Aggregation data from API response
 * @param {Object} props.filterLabels - Labels for cold start restoration
 * @returns {JSX.Element}
 */
const FilterSection = ({
  filterConfigs,
  filters,
  onFilterChange,
  document,
  className = 'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  aggregations,
  filterLabels,
}) => {
  return (
    <div className={className}>
      {filterConfigs.map((config) => (
        <div key={config.key}>
          <FilterSelect
            config={config}
            filters={filters}
            onFilterChange={onFilterChange}
            document={document}
            aggregations={aggregations}
            filterLabels={filterLabels}
          />
        </div>
      ))}
    </div>
  );
};

export default FilterSection;
